import React from "react";
import { Bar } from "react-chartjs-2";
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, LineController, LineElement, PointElement, Tooltip } from "chart.js";
import { useApiData } from "../hooks/useApiData";
import { unwrapArray } from "../utils/api";
import { exportSectionsToExcel, exportSectionsToPDF, printSections } from "../utils/exportUtils";
import { addDays, dayStats, fillDays, longDate, shortDate, weekdayIndex, WEEKDAYS_LONG, WEEKDAYS_SHORT } from "../utils/dailySales";
import { formatDateRange } from "../utils/date";
import { formatNumber, getChartColors, tooltipStyle } from "./charts/chartTheme";
import ExportButtonGroup from "./ui/ExportButtonGroup";
import LoadingOverlay from "./ui/LoadingOverlay";
import { ChartSkeleton } from "./ui/Skeleton";
import { EmptyState, ErrorState } from "./ui/StateBlock";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineController, LineElement, PointElement, Tooltip, Legend);

function compact(v) {
  const abs = Math.abs(v);
  if (abs >= 1e6) return `${(v / 1e6).toLocaleString("sr-RS", { maximumFractionDigits: 1 })}M`;
  if (abs >= 1e3) return `${(v / 1e3).toLocaleString("sr-RS", { maximumFractionDigits: 0 })}k`;
  return String(v);
}

/**
 * Revenue per day. A single selected day is compared with the same weekday of the previous 4 weeks
 * (one bar per week); a longer period shows one bar per day, weekends tinted, with an average line
 * and the comparison period as faint bars aligned by day number.
 */
export default function DailySalesPanel({ startDate, endDate, compareStart, compareEnd, compareLabel, warehouse, darkMode }) {
  const colors = getChartColors(darkMode);
  const whParam = warehouse ? `&warehouse=${warehouse}` : "";
  const singleDay = startDate === endDate;
  const fetchStart = singleDay ? addDays(startDate, -28) : startDate;

  const main = useApiData(`/daily_sales.php?od=${fetchStart}&do=${endDate}${whParam}`);
  const cmp = useApiData(!singleDay && compareStart && compareEnd ? `/daily_sales.php?od=${compareStart}&do=${compareEnd}${whParam}` : null);

  let days = fillDays(fetchStart, endDate, unwrapArray(main.data));
  if (singleDay) {
    const wd = weekdayIndex(startDate);
    days = days.filter((d) => weekdayIndex(d.date) === wd);
  }
  const cmpDays = !singleDay && compareStart && compareEnd && cmp.data ? fillDays(compareStart, compareEnd, unwrapArray(cmp.data)) : [];

  if (main.error && days.every((d) => d.total === 0)) return <ErrorState />;
  if (main.loading && !main.data) return <ChartSkeleton height={260} />;
  if (days.every((d) => d.total === 0)) return <EmptyState title="Nema prometa u izabranom periodu" hint="Probaj drugi period ili magacin." />;

  const stats = dayStats(days);
  const totalRevenue = days.reduce((s, d) => s + d.total, 0);
  const totalBills = days.reduce((s, d) => s + d.bills, 0);
  const labels = days.map((d) => (singleDay ? `${WEEKDAYS_SHORT[weekdayIndex(d.date)]} ${shortDate(d.date)}` : days.length <= 31 ? shortDate(d.date) : shortDate(d.date)));
  const isWeekend = (date) => weekdayIndex(date) >= 5;
  const barColor = (d) => (singleDay ? (d.date === startDate ? colors.accent : colors.muted) : isWeekend(d.date) ? `${colors.accent}80` : colors.accent);

  const datasets = [
    {
      type: "bar",
      label: "Promet (RSD)",
      data: days.map((d) => d.total),
      backgroundColor: days.map(barColor),
      borderRadius: 3,
      barPercentage: 0.85,
      categoryPercentage: 0.8,
      order: 2,
    },
  ];
  if (cmpDays.length > 0) {
    datasets.unshift({
      type: "bar",
      label: compareLabel || "Poređenje",
      data: days.map((_, i) => (cmpDays[i] ? cmpDays[i].total : null)),
      backgroundColor: `${colors.muted}66`,
      borderRadius: 3,
      barPercentage: 0.85,
      categoryPercentage: 0.8,
      order: 3,
    });
  }
  if (stats && !singleDay) {
    datasets.push({
      type: "line",
      label: "Prosek po danu",
      data: days.map(() => stats.average),
      borderColor: colors.textStrong,
      borderDash: [6, 4],
      borderWidth: 1.5,
      pointRadius: 0,
      pointHoverRadius: 0,
      order: 1,
    });
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { position: "top", align: "end", labels: { color: colors.text, boxWidth: 8, boxHeight: 8, usePointStyle: true } },
      datalabels: { display: false },
      tooltip: {
        ...tooltipStyle(colors),
        callbacks: {
          title: (items) => {
            const d = days[items[0].dataIndex];
            return `${WEEKDAYS_LONG[weekdayIndex(d.date)]}, ${longDate(d.date)}`;
          },
          label: (ctx) => {
            const d = days[ctx.dataIndex];
            if (ctx.dataset.label === "Promet (RSD)") {
              const avg = d.bills ? ` | prosečan račun ${formatNumber(d.total / d.bills)}` : "";
              return [`Promet: ${formatNumber(d.total)} RSD`, `Računa: ${formatNumber(d.bills)}${avg}`];
            }
            if (ctx.dataset.type === "line") return `Prosek po danu: ${formatNumber(ctx.raw)} RSD`;
            const c = cmpDays[ctx.dataIndex];
            return c ? `${compareLabel || "Poređenje"} (${shortDate(c.date)}): ${formatNumber(c.total)} RSD` : "";
          },
        },
      },
    },
    scales: {
      x: { border: { display: false }, grid: { display: false }, ticks: { color: colors.text, maxRotation: 0, autoSkip: true, maxTicksLimit: 14 } },
      y: { border: { display: false }, grid: { color: colors.grid, drawTicks: false }, ticks: { color: colors.text, padding: 8, callback: (v) => compact(v) } },
    },
  };

  const rangeText = formatDateRange(startDate, endDate);
  const section = () => ({
    heading: singleDay ? `Ista dan u nedelji, poslednjih 5 nedelja` : rangeText,
    head: ["Datum", "Dan", "Promet (RSD)", "Računa", "Prosečan račun"],
    body: days.map((d) => [longDate(d.date), WEEKDAYS_LONG[weekdayIndex(d.date)], d.total, d.bills, d.bills ? Math.round(d.total / d.bills) : 0]),
    totalRow: ["Ukupno", "", totalRevenue, totalBills, totalBills ? Math.round(totalRevenue / totalBills) : 0],
    decimals: [null, null, 0, 0, 0],
  });
  const title = "Promet po danima";
  const exportExcel = () => exportSectionsToExcel({ title, sections: [section()], filename: "promet_po_danima.xlsx", sheetName: "Promet po danima" });
  const exportPdf = () => exportSectionsToPDF({ title, sections: [section()], filename: "promet_po_danima.pdf" });
  const print = () => printSections({ title, sections: [section()] });

  return (
    <LoadingOverlay loading={main.loading}>
      <div className="flex flex-col gap-4">
        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Stat label="Najbolji dan" value={`${formatNumber(stats.best.total)} RSD`} hint={`${WEEKDAYS_SHORT[weekdayIndex(stats.best.date)]} ${shortDate(stats.best.date)}`} />
          <Stat label="Najslabiji dan" value={`${formatNumber(stats.worst.total)} RSD`} hint={`${WEEKDAYS_SHORT[weekdayIndex(stats.worst.date)]} ${shortDate(stats.worst.date)}`} />
          <Stat label="Prosek po danu" value={`${formatNumber(stats.average)} RSD`} hint={`${stats.soldDays} ${stats.soldDays === 1 ? "dan" : "dana"} sa prometom`} />
        </dl>

        {singleDay && (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Izabrani dan ({WEEKDAYS_LONG[weekdayIndex(startDate)].toLowerCase()}) uporedjen je sa istim danom u prethodne 4 nedelje.
          </p>
        )}
        {!singleDay && (
          <p className="text-xs text-slate-500 dark:text-slate-400">Vikendi su svetlije obojeni. Isprekidana linija je prosek po danu.</p>
        )}

        <div className="relative w-full min-w-0 overflow-hidden" style={{ height: 260 }}>
          <Bar data={{ labels, datasets }} options={options} />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Promet po danima</h4>
          <ExportButtonGroup size="sm" onExcel={exportExcel} onPdf={exportPdf} onPrint={print} />
        </div>
        <div className="max-h-72 overflow-auto rounded-lg border border-slate-200 dark:border-slate-800">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-100 text-left text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              <tr>
                <th className="px-3 py-2 font-medium">Datum</th>
                <th className="px-3 py-2 font-medium">Dan</th>
                <th className="px-3 py-2 text-right font-medium">Promet (RSD)</th>
                <th className="px-3 py-2 text-right font-medium">Računa</th>
                <th className="px-3 py-2 text-right font-medium">Prosečan račun</th>
              </tr>
            </thead>
            <tbody>
              {days.map((d) => (
                <tr key={d.date} className="border-t border-slate-100 dark:border-slate-800/60">
                  <td className="px-3 py-1.5 tabular-nums">{longDate(d.date)}</td>
                  <td className="px-3 py-1.5 text-slate-600 dark:text-slate-300">{WEEKDAYS_LONG[weekdayIndex(d.date)]}</td>
                  <td className="px-3 py-1.5 text-right font-medium tabular-nums">{formatNumber(d.total)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{formatNumber(d.bills)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{d.bills ? formatNumber(d.total / d.bills) : "-"}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="sticky bottom-0 bg-slate-100 font-semibold dark:bg-slate-800">
              <tr>
                <td className="px-3 py-2" colSpan={2}>Ukupno</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatNumber(totalRevenue)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatNumber(totalBills)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{totalBills ? formatNumber(totalRevenue / totalBills) : "-"}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </LoadingOverlay>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div className="rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-800">
      <dt className="text-xs text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className="mt-0.5 text-base font-semibold tabular-nums text-slate-900 dark:text-slate-50">{value}</dd>
      <dd className="text-xs text-slate-500 dark:text-slate-400">{hint}</dd>
    </div>
  );
}
